import { useCallback } from 'react';
import { useHistory } from 'react-router';
import URL_PARAMS, {
  DataTableURLParamKeys,
} from 'components/v2/DataTable/data/urlParams';
import { SortedColumn } from 'components/v2/DataTable/DataTable';
import getSortingDirection from 'components/v2/DataTable/helpers/getSortingDirection';
import { useScrollRestoration } from 'infrastructure/scrollRestoration/ScrollRestoration';

type ParamKeyValuePairs = Record<string, string>;

interface UseURLParamsOptions {
  useRouterHistory?: boolean;
}

const useURLParams = ({
  useRouterHistory = false,
}: UseURLParamsOptions = {}) => {
  const history = useHistory();
  const { saveScrollPosition } = useScrollRestoration();
  const createCurrentURL = useCallback(
    () =>
      useRouterHistory
        ? new URL(history.createHref(history.location), window.location.origin)
        : new URL(window.location.href),
    [history, useRouterHistory]
  );
  const updateURL = useCallback(
    (url: URL, keepHistory = false) => {
      saveScrollPosition();

      if (useRouterHistory) {
        history[keepHistory ? 'push' : 'replace']({
          ...history.location,
          pathname: url.pathname,
          search: url.search,
          hash: url.hash,
        });
        return;
      }

      window.history[keepHistory ? 'pushState' : 'replaceState'](null, '', url);
    },
    [history, saveScrollPosition, useRouterHistory]
  );

  const handleURLSearchQueryParam = useCallback(
    (searchText?: string) => {
      const url = createCurrentURL();
      if (searchText) {
        url.searchParams.set(URL_PARAMS.textSearch, `${searchText}`);
      } else {
        url.searchParams.delete(URL_PARAMS.textSearch);
      }

      const urlHasToChange =
        url.search !== history.location.search ||
        (!useRouterHistory && url.toString() !== window.location.href);
      if (urlHasToChange) {
        updateURL(url);
      }
    },
    [createCurrentURL, history.location.search, updateURL, useRouterHistory]
  );

  const setURLParams = useCallback(
    (paramNames: ParamKeyValuePairs, keepHistory?: boolean): string => {
      const url = createCurrentURL();
      const params = Object.entries(paramNames);
      params.forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
      const urlHasToChange =
        url.search !== history.location.search ||
        (!useRouterHistory && url.toString() !== window.location.href);
      if (urlHasToChange) {
        updateURL(url, keepHistory);
      }
      return url.toString();
    },
    [createCurrentURL, history.location.search, updateURL, useRouterHistory]
  );

  const setURLSortingParams = useCallback(
    (
      sortingParams: SortedColumn,
      paramKeys: Pick<DataTableURLParamKeys, 'sortId' | 'sorting'> = URL_PARAMS,
      keepHistory = false
    ): void => {
      const url = createCurrentURL();
      const { id, desc } = sortingParams;
      url.searchParams.set(paramKeys.sortId, `${id}`);
      url.searchParams.set(paramKeys.sorting, `${getSortingDirection(desc)}`);
      const urlHasToChange =
        url.search !== history.location.search ||
        (!useRouterHistory && url.toString() !== window.location.href);
      if (urlHasToChange) {
        updateURL(url, keepHistory);
      }
    },
    [createCurrentURL, history.location.search, updateURL, useRouterHistory]
  );

  const unsetURLParams = useCallback(
    (params: string[]): string => {
      const url = createCurrentURL();
      params.forEach(paramName => {
        url.searchParams.delete(paramName);
      });
      const urlHasToChange =
        url.search !== history.location.search ||
        (!useRouterHistory && url.toString() !== window.location.href);
      if (urlHasToChange) {
        updateURL(url);
      }
      return url.toString();
    },
    [createCurrentURL, history.location.search, updateURL, useRouterHistory]
  );

  const unsetURLSortingParams = useCallback(
    (
      paramKeys: Pick<DataTableURLParamKeys, 'sortId' | 'sorting'> = URL_PARAMS,
      keepHistory = false
    ) => {
      const url = createCurrentURL();
      url.searchParams.delete(paramKeys.sortId);
      url.searchParams.delete(paramKeys.sorting);
      const urlHasToChange =
        url.search !== history.location.search ||
        (!useRouterHistory && url.toString() !== window.location.href);
      if (urlHasToChange) {
        updateURL(url, keepHistory);
      }
    },
    [createCurrentURL, history.location.search, updateURL, useRouterHistory]
  );

  return {
    handleURLSearchQueryParam,
    setURLParams,
    setURLSortingParams,
    unsetURLParams,
    unsetURLSortingParams,
  };
};

export default useURLParams;
